import React from "react";
import { useTranslation } from "react-i18next";
import { Caption1, PencilSmallIcon } from "@activecollab/components";
import PropTypes from "prop-types";
import SectionLoader from "~/components/SectionLoader/SectionLoader";
import UserLink from "~/components/UserLink/UserLink";
import { useHistoryOfChanges } from "~/repository";
import { descSortCompare } from "~/utils/utils.jsx";
import Ago from "../component.ago.jsx";
import { DescriptionWrapper } from "../ModalObjects/DescriptionWrapper.js";

const HistoryItem = ({ historyItem, parentType, parentId }) => {
  const { t } = useTranslation();

  return (
    <div data-testid="history-diff-list-item">
      <div className="tw-flex tw-items-center tw-gap-1 tw-mb-3">
        <PencilSmallIcon />
        <Caption1 color="secondary">
          {t("Edited")}
          {": "}
          <Ago timestamp={historyItem.timestamp} />
          {" - "}
        </Caption1>
        <UserLink
          id={historyItem.created_by_id}
          name={historyItem.created_by_name}
          nameProps={{ color: "secondary" }}
          context="object history diff"
          hideAvatar
          short
          renderAs={Caption1}
          notInteractive
        />
      </div>
      <DescriptionWrapper
        parentId={parentId}
        parentType={parentType}
        body={historyItem.modifications["body"][3]}
        className="tw-mb-6 tw-border-b tw-border-solid tw-border-0 tw-border-border-primary tw-pb-6"
      />
    </div>
  );
};

HistoryItem.propTypes = {
  historyItem: PropTypes.object,
  id: PropTypes.number,
  parentType: PropTypes.string,
  parentId: PropTypes.number,
};

const ObjectHistoryDiff = ({ objectType, objectId, parentType, parentId }) => {
  const { t } = useTranslation();
  const args = {
    verbose: 1,
    fields: "body",
    diff_for_fields: "body",
  };
  const { data, isLoading } = useHistoryOfChanges(
    objectType,
    objectId,
    {
      select: (response) => response.sort((a, b) => descSortCompare(a.timestamp, b.timestamp)),
    },
    args
  );

  return (
    <div>
      {isLoading && <SectionLoader className="tw-flex tw-flex-1 tw-mt-4" />}
      {data && data.length > 0 ? (
        <>
          <div className="tw-flex tw-flex-col">
            {data.map((historyItem, index) => {
              return (
                <HistoryItem
                  key={`history-${index}`}
                  historyItem={historyItem}
                  id={objectId}
                  parentType={parentType}
                  parentId={parentId}
                />
              );
            })}
          </div>
          <Caption1 weight="bold">{t("Original comment")}:</Caption1>
          <DescriptionWrapper
            parentId={parentId}
            parentType={parentType}
            body={data[data.length - 1].modifications["body"][0]}
          />
        </>
      ) : null}
    </div>
  );
};

ObjectHistoryDiff.defaultProps = {};

ObjectHistoryDiff.propTypes = {
  objectType: PropTypes.string.isRequired,
  objectId: PropTypes.number.isRequired,
  projectId: PropTypes.number,
  parentType: PropTypes.string.isRequired,
  parentId: PropTypes.number.isRequired,
};

export default ObjectHistoryDiff;
